import { IonLabel } from "@ionic/react";
import React from "react";
import { useProfile } from "../data/store/useProfile";
import "./CompactToggle.css";

/**
 * Compact-view switch.
 *
 * Implemented as a single native <button role="switch"> rather than an Ionic
 * <ion-toggle> because Ionic 5.4.0's toggle nests a focusable button inside a
 * role="checkbox" host, which axe flags as `nested-interactive` (serious) — see
 * SON-22. A native button gives us correct keyboard activation (Space/Enter)
 * and one interactive node; `role="switch"` + `aria-checked` convey state to
 * assistive tech. The visual is a faithful copy of ion-toggle (CompactToggle.css).
 *
 * The switch's right edge lines up with the Play All button below (which
 * lives outside this card entirely) via a 2-column CSS grid on
 * `sortWrapper` — the label sits left (`auto` column, flush with the row's
 * left edge), and the switch (right column, `1fr`, `justify-self: end`)
 * lands on the grid's own right edge. `sortWrapper` itself is widened by
 * `edgeInset` to cancel this card's own padding so that edge coincides
 * with the card's true outer edge, matching Play All — but each page wraps
 * CompactToggle in a differently-padded card (0 on the main feed, 5 on
 * SongsPage, 20 on ArtistDetail), so a single hardcoded value overshoots
 * on some pages and undershoots on others. Callers must pass the *actual*
 * rendered padding of their own wrapping card (confirmed via
 * getComputedStyle, not assumed from the inline `padding` style — Ionic
 * components don't always render padding literally as set).
 *
 * A prior version used `position: absolute; top: 50%` to place the switch,
 * anchored to the nearest positioned ancestor (the whole profile card).
 * That broke for any artist whose card is taller than a couple of rows —
 * 50% of the WHOLE card's height has nothing to do with where this
 * specific row renders inside it — and depended on an ancestor
 * (`.centerColumn`) that only ArtistDetail's card actually has. Grid
 * placement is normal document flow: it vertically centers with the label
 * via `align-items: center` on their shared row, correct regardless of
 * how much content precedes this component or which page renders it.
 */
export const CompactToggle: React.FC<{ edgeInset?: number }> = ({ edgeInset = 0 }) => {
  const { compact, setCompactMode } = useProfile();

  return (
    <div
      className="sortWrapper"
      style={{ "--sw-edge-inset": `${edgeInset}px` } as React.CSSProperties}
    >
      <IonLabel
        color={compact ? "primary" : undefined}
        className={compact ? "" : "text-secondary"}
      >
        Compact view
      </IonLabel>
      <button
        type="button"
        role="switch"
        aria-checked={compact}
        aria-label="Toggle compact view"
        className={`compact-switch${compact ? " compact-switch--checked" : ""}`}
        onClick={() => setCompactMode(!compact)}
      >
        <span className="compact-switch__track">
          <span className="compact-switch__handle" />
        </span>
      </button>
    </div>
  );
};
